import { Id } from '../aliases'
import {
  deserialize,
  serialize,
  type CborSchema,
} from '@mesh/friendly-cbor-serializer'
import AgentMessageTypes from './agentMessageTypes'
import { MeshAgent } from './meshAgent'
import {
  type GetProfileResponse,
  GetProfileResponseSchema,
  type GetProfileRequestPayload,
  GetProfileRequestPayloadSchema,
  type SpaceGetApplicationsResponse,
  SpaceGetApplicationsResponseSchema,
  type SpaceGetApplicationsRequestPayload,
  SpaceGetApplicationsRequestPayloadSchema,
  type LookupSiteResponse,
  LookupSiteResponseSchema,
  type LookupSiteRequestPayload,
  LookupSiteRequestPayloadSchema,
  ListHumanProxiesResponse,
  ListHumanProxiesResponseSchema,
  ListHumanProxiesRequestPayloadSchema,
  MeshoutResponse,
  MeshoutResponseSchema,
} from './messages'
import {
  CheckMeshfaStatusRequestPayload,
  CheckMeshfaStatusRequestPayloadSchema,
  CheckMeshfaStatusResponse,
  CheckMeshfaStatusResponseSchema,
} from './messages/checkMeshfaStatus'

export default class MessagingService {
  private meshAgent: MeshAgent

  constructor(meshAgent: MeshAgent) {
    this.meshAgent = meshAgent
  }

  private async send<TPayload, TResponse>(params: {
    requestPayload?: TPayload
    requestSchema?: CborSchema<TPayload>
    messageType: number
    responseSchema: CborSchema<TResponse>
  }): Promise<TResponse> {
    const {
      requestPayload = {},
      requestSchema,
      messageType,
      responseSchema,
    } = params
    const cbor = requestSchema
      ? serialize(requestSchema, requestPayload)
      : new Map()
    const messageId = crypto.getRandomValues(new Uint8Array(32))
    const cborResponse = await this.meshAgent.buildAndSendMessage({
      payload: cbor,
      messageType: messageType,
      messageId,
    })
    return deserialize(responseSchema, cborResponse)
  }

  async getProfile(humanProxyId: Id): Promise<GetProfileResponse> {
    const requestPayload: GetProfileRequestPayload = {
      humanProxyId,
    }
    return this.send({
      requestPayload,
      requestSchema: GetProfileRequestPayloadSchema,
      messageType: AgentMessageTypes.GetProfileRequestType,
      responseSchema: GetProfileResponseSchema,
    })
  }

  async spaceGetApplications(
    humanProxyId: Id,
    spaceId: Id,
  ): Promise<SpaceGetApplicationsResponse> {
    const requestPayload: SpaceGetApplicationsRequestPayload = {
      humanProxyId,
      spaceId,
    }
    return this.send({
      requestPayload,
      requestSchema: SpaceGetApplicationsRequestPayloadSchema,
      messageType: AgentMessageTypes.SpaceGetApplicationsRequestType,
      responseSchema: SpaceGetApplicationsResponseSchema,
    })
  }

  async lookupSite(
    siteName: string,
    includeFileHashes: boolean = true,
    version: string = 'CURRENT',
  ): Promise<LookupSiteResponse> {
    const requestPayload: LookupSiteRequestPayload = {
      siteName,
      includeFileHashes,
      version,
    }
    return this.send({
      requestPayload,
      requestSchema: LookupSiteRequestPayloadSchema,
      messageType: AgentMessageTypes.LookupSiteInfoRequestType,
      responseSchema: LookupSiteResponseSchema,
    })
  }

  async listHumanProxies(): Promise<ListHumanProxiesResponse> {
    return this.send({
      requestSchema: ListHumanProxiesRequestPayloadSchema,
      messageType: AgentMessageTypes.ListHumanProxiesRequestType,
      responseSchema: ListHumanProxiesResponseSchema,
    })
  }

  async checkMeshfaStatus(
    triggerReauthIfNeeded: boolean = false,
    forceTrigger: boolean = false,
  ): Promise<CheckMeshfaStatusResponse> {
    const requestPayload: CheckMeshfaStatusRequestPayload = {
      triggerReauthIfNeeded,
      forceTrigger,
    }
    return this.send({
      requestPayload,
      requestSchema: CheckMeshfaStatusRequestPayloadSchema,
      messageType: AgentMessageTypes.CheckMeshfaStatusRequestType,
      responseSchema: CheckMeshfaStatusResponseSchema,
    })
  }

  async meshout(): Promise<MeshoutResponse> {
    return this.send({
      messageType: AgentMessageTypes.MeshoutRequestType,
      responseSchema: MeshoutResponseSchema,
    })
  }
}
